import axios from 'axios';
import { getIdToken } from '@/pages/MyAccount/components/firebaseIdToken';

/**
 * "Request to enable" for a feature the practice hasn't paid for.
 *
 * The backend (myaccount-service/feature-request.js) emails ONLY nilay@teamio.app and resolves the
 * site, employee and time from the verified ID token — so this sends nothing but the feature key and
 * the page it was clicked on. Never add a recipient/sender/body here; the server ignores them.
 */

export const FEATURE_REQUEST_EMAIL = 'nilay@teamio.app';
export const FEATURE_REQUEST_COOLDOWN_MS = 24 * 60 * 60 * 1000;

export function featureRequestCooldownKey(siteID, featureKey) {
  return `feature_enable_request_${siteID}_${featureKey}`;
}

/** True when this site+feature was requested from this browser within the cooldown. */
export function wasRecentlyRequested(storage, siteID, featureKey, now = Date.now()) {
  try {
    const raw = storage && storage.getItem(featureRequestCooldownKey(siteID, featureKey));
    const at = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(at) && now - at < FEATURE_REQUEST_COOLDOWN_MS;
  } catch (e) {
    return false;
  }
}

export function markRequested(storage, siteID, featureKey, now = Date.now()) {
  try {
    if (storage) storage.setItem(featureRequestCooldownKey(siteID, featureKey), String(now));
  } catch (e) {
    // Private mode / blocked storage: the request still went out; only the cooldown is lost.
  }
}

/** Fallback when the request can't be sent — opens the user's mail app, addressed to Teamio. */
export function featureRequestMailto({ featureLabel, siteName, sourcePage }) {
  const subject = `Teamio request to enable feature ${featureLabel}`;
  const body = `Hi Teamio,\n\nWe'd like to enable ${featureLabel}.\n\nPractice: ${siteName || ''}\nRequested from: ${sourcePage || ''}\n`;
  return `mailto:${FEATURE_REQUEST_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export async function requestFeatureEnable({ auth, siteID, featureKey, sourcePage }) {
  const idToken = await getIdToken(auth);
  const { data } = await axios.post(
    '/request-feature-enable',
    { siteID, featureKey, sourcePage, idToken },
    { timeout: 15000 }
  );
  return data;
}
